import React from 'react';
import { Link } from 'react-router-dom';
import { Ticket, Clock } from 'lucide-react';
import { CountdownTimer } from '../common/CountdownTimer';
import { formatCurrency } from '../../utils/formatters';
import { Raffle } from '../../types';

interface RaffleCardProps {
  raffle: Raffle;
}

export const RaffleCard: React.FC<RaffleCardProps> = ({ raffle }) => {
  const pot = raffle.nextDraw?.sales ?? raffle.currentDrawSales ?? 0;
  const tickets = raffle.nextDraw?.ticketsSold ?? raffle.currentDrawTicketsSold ?? 0;
  const nextDrawTime = raffle.nextDraw?.scheduledTime;

  return (
    <Link to={`/raffles/${raffle.id}`} className="block group">
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
        {/* Image or gradient placeholder */}
        <div className="relative h-44 overflow-hidden">
          {raffle.imageUrl ? (
            <img src={raffle.imageUrl} alt={raffle.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-700" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

          {/* Price badge — top right */}
          <div className="absolute top-3 right-3 bg-white dark:bg-gray-900 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-md border border-gray-100 dark:border-gray-700">
            <span className="text-base font-bold text-indigo-600 dark:text-indigo-400">{formatCurrency(raffle.ticketPrice, raffle.currency)}</span>
          </div>

          {/* Pot — bottom of image, large and prominent */}
          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">Current Pot</p>
              <p className="text-2xl font-extrabold text-white leading-tight">{formatCurrency(pot, raffle.currency)}</p>
            </div>
            {nextDrawTime && (
              <div className="bg-gray-900/80 backdrop-blur-sm rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 border border-white/10">
                <Clock className="w-3 h-3 text-emerald-400" />
                <CountdownTimer target={nextDrawTime} variant="card" onDark />
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1 group-hover:text-indigo-600 transition-colors line-clamp-1">{raffle.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3 leading-relaxed">{raffle.description}</p>

          {/* Bottom row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-indigo-50 dark:bg-indigo-950 flex items-center justify-center">
                <Ticket className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-300"><span className="font-bold text-gray-900 dark:text-gray-100">{tickets}</span> tickets</span>
            </div>
            <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 transition-colors">
              View →
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};
